import React from 'react';
import { Form, Select, Input, Button } from 'antd';
import { FormInstance } from 'antd/es/form';
import FormSection from '../FormSection';
import CurvDown from '../svg/CurvDown';

const { Option } = Select;

interface SelecteesInformationProps {
  form: FormInstance;
  isViewMode?: boolean;
}

const SelecteesInformation: React.FC<SelecteesInformationProps> = ({ form, isViewMode }) => {
  const isView = isViewMode || new URLSearchParams(window.location.search).get('view') === 'true';
  const yesNoOptions = ['Yes', 'No'];
  const discriminationBasisOptions = [
    'Age', 'Color', 'Mental/Physical Handicap', 'National Origin',
    'Race', 'Religion', 'Reprisal', 'Sex'
  ];

  const sameofficialWatch = Form.useWatch(['selectees_info', 'same_selecting_official'], form);
  const disableDifferentOfficial = sameofficialWatch !== 'No';

  return (
    <div className="flex flex-col w-full h-full mb-[10px] lg:mb-0 selectees-info-container">
      <style dangerouslySetInnerHTML={{
        __html: `
        .selectees-info-container .ant-form-item-label {
          min-height: 30px !important;
          margin-bottom: 8px !important;
        }
        .selectees-info-container .basis-field .ant-form-item-explain-error {
          margin-top: 28px !important;
        }
        @media (max-width: 768px) {
          .selectees-info-container .ant-form-item-label {
            min-height: unset !important;
            margin-bottom: 0px !important;
            display: block !important;
          }
        }
      `}} />
      <FormSection title="Selectees Information" columns={1}>
        <div className="flex flex-col lg:gap-10 w-full lg:w-[75%]">
          <Form.Item
            label="Were other applicants selected for the position?"
            name={['selectees_info', 'other_applicants_selected']}
            className="pb-[41px] lg:pb-0 max-w-[400px]"
          >
            <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
              {yesNoOptions.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>

          <div className="lg:mt-2">
            <Form.List name={['selectees_info', 'coapplicants']}>
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-0 w-full">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] leading-[29px] m-0">
                      Name your coapplicants
                    </h3>
                    <Button
                      type="text"
                      onClick={() => {
                        if (fields.length < 3) {
                          add();
                        }
                      }}
                      disabled={fields.length >= 3 || isViewMode}
                      className="flex items-center gap-2 text-[#4682B4] font-['Lexend',sans-serif] p-0 disabled:opacity-50"
                    >
                      <span className="text-[18px] leading-none">+</span> Add coapplicants
                    </Button>
                  </div>

                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex flex-row items-start gap-0 w-full mb-[28px] md:mb-6">
                      {/* Number */}
                      <div className="w-8 flex justify-center pt-[42px] md:pt-[50px] mr-[8px] md:mr-[18px]">
                        <span className="font-['Lexend',sans-serif] text-[12px] md:text-[18px] text-[#1F2A37A1] font-medium">
                          {String(name + 1).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Name Field */}
                      <div className="flex-1 mr-[20px] md:mr-[60px]">
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Name"
                          className="mb-0"
                          rules={[{ required: true, message: 'Please enter the name' }]}
                        >
                          <Input placeholder="Name" />
                        </Form.Item>
                      </div>

                      {/* Bases Field */}
                      <div className="flex-1 mr-[10px] md:mr-[31px]">
                        <Form.Item
                          {...restField}
                          name={[name, 'basis']}
                          label="Basis"
                          className="mb-0 basis-field"
                          rules={[{ required: true, message: 'Please select a basis' }]}
                        >
                          <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                            {discriminationBasisOptions.map(option => (
                              <Option key={option} value={option}>{option}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>

                      {/* Delete */}
                      <div className="w-10 flex justify-center pt-[42px] md:pt-[50px]">
                        <button
                          onClick={() => {
                            if (!isView) {
                              remove(name);
                            }
                          }}
                          disabled={isView}
                          type="button"
                          style={isView ? { pointerEvents: 'none', opacity: 0.3, cursor: 'not-allowed' } : undefined}
                          className="p-1 text-[#6FB6F2] hover:text-red-500 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                          <svg width="20" height="22" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[12px] h-[13px] md:w-[18px] md:h-[20px] stroke-current">
                            <path d="M1 4h14M2 4v11a2 2 0 002 2h8a2 2 0 002-2V4M5 4V2a1 1 0 011-1h4a1 1 0 011 1v2M6.5 8v5M9.5 8v5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-[60px] lg:gap-y-[37px] lg:w-[87%]">
            <div className="flex flex-col">
              <div className="flex items-end lg:min-h-[80px] mb-[14px] lg:mb-2">
                <label className="font-['Lexend',sans-serif] text-[14px] lg:text-[16px] text-[#1F2A37] font-medium">
                  Were the selected persons selected by the same selecting officials?
                </label>
              </div>
              <Form.Item
                name={['selectees_info', 'same_selecting_official']}
                className="pb-[28px] lg:pb-0 w-full"
              >
                <Select
                  placeholder="Select"
                  suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                  onChange={(val) => {
                    if (val === 'Yes') {
                      form.setFieldValue(['selectees_info', 'different_selecting_official_name'], undefined);
                    }
                  }}
                >
                  {yesNoOptions.map(option => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className="flex flex-col">
              <div className="flex items-end lg:min-h-[80px] mb-[14px] lg:mb-2">
                <label className="font-['Lexend',sans-serif] text-[14px] md:text-[16px] text-[#1F2A37] font-medium">
                  Different selecting officials name
                </label>
              </div>
              <Form.Item
                name={['selectees_info', 'different_selecting_official_name']}
                className="mb-0 w-full"
              >
                <Input placeholder="Enter Name" disabled={disableDifferentOfficial} />
              </Form.Item>
            </div>
          </div>
        </div>
      </FormSection>
    </div>
  );
};

export default SelecteesInformation;

